import { useCallback } from 'react';
import { toast } from 'react-toastify';
import useResendGiftCardEmail from './useResendGiftCardEmail';
import useClearGiftCard from './useClearGiftCard';
import useStateCooldown from '../useStateCooldown';
import { GiftCard } from '../../types/gift-cards';
import { FooterAction } from '../../interfaces';

export type GiftCardActionOption = FooterAction & {
  id?: string;
  disabled?: boolean;
};

export type GiftCardActionTarget = Pick<
  GiftCard,
  'id' | 'currentBalance' | 'customerId' | 'customer'
>;

function useGiftCardActions() {
  const [isCooldown, setIsCooldown] = useStateCooldown(15_000);

  const {
    mutate: resendGiftCard,
    isPending: isResending,
    variables: resendVariables,
  } = useResendGiftCardEmail({
    onSuccess: () => {
      setIsCooldown(true);
      toast.success('Gift card sent successfully!');
    },
    onError: () => {
      toast.error('Failed to resend gift card email');
    },
  });

  const { mutate: clearGiftCard, isPending: isClearing } = useClearGiftCard({
    onSuccess: () => {
      toast.success('Gift card balance cleared successfully!');
    },
    onError: error => {
      toast.error(error);
    },
  });

  const getOptions = useCallback(
    (giftCard: GiftCardActionTarget): GiftCardActionOption[] =>
      [
        !!giftCard.customer && {
          id: 'resend',
          label: 'Resend e-gift card',
          action: () => {
            if (isCooldown) return;
            resendGiftCard(giftCard);
          },
          disabled:
            isCooldown || (isResending && resendVariables?.id === giftCard.id),
        },
        giftCard.currentBalance > 0 && {
          id: 'clear',
          label: 'Clear balance',
          action: () => clearGiftCard(giftCard),
          disabled: isClearing,
        },
      ].filter(Boolean) as GiftCardActionOption[],
    [
      isCooldown,
      isResending,
      isClearing,
      resendGiftCard,
      clearGiftCard,
      resendVariables?.id,
    ],
  );

  return { getOptions, isResending, isClearing, isCooldown };
}

export default useGiftCardActions;
